/**
 * Environment Banner (#1448)
 *
 * Displays a warning banner on QA and Dev environments to prevent
 * users from accidentally using the wrong environment.
 * Production renders nothing (returns null — no wrapper, no spacing).
 *
 * The production URL (oro.fit) is intentionally hardcoded: this component's
 * sole purpose is directing users FROM non-prod TO the known production domain.
 * APP_BASE_URL on QA/Dev points to the non-prod domain, so it can't be used here.
 */

import { AlertTriangle } from 'lucide-react';

type EnvType = 'dev' | 'qa' | 'production';

function detectEnvironment(): EnvType {
  const hostname = window.location.hostname;
  if (hostname.startsWith('dev.') || hostname === 'localhost') return 'dev';
  if (hostname.startsWith('qa.')) return 'qa';
  return 'production';
}

const config: Record<Exclude<EnvType, 'production'>, { label: string; bg: string; text: string }> = {
  dev: {
    label: 'DEV ENVIRONMENT',
    bg: 'bg-oro-error-soft',
    text: 'text-oro-error',
  },
  qa: {
    label: 'QA ENVIRONMENT',
    bg: 'bg-oro-warning-soft',
    text: 'text-oro-warning',
  },
};

interface EnvironmentBannerProps {
  className?: string;
}

export default function EnvironmentBanner({ className }: EnvironmentBannerProps) {
  const env = detectEnvironment();
  if (env === 'production') return null;

  const { label, bg, text } = config[env];

  return (
    <div role="status" className={`${bg} ${text} px-4 py-2.5 text-center text-sm font-medium${className ? ` ${className}` : ''}`}>
      <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
      {label} — This is for testing only.{' '}
      <a
        href="https://oro.fit"
        className="underline font-semibold hover:opacity-80"
      >
        Use oro.fit for your real club.
      </a>
    </div>
  );
}
